import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { FaAngleLeft } from "react-icons/fa6";
import { cn } from "@/utils/cn";
import { FiCreditCard } from "react-icons/fi";
import { MdNotificationsNone } from "react-icons/md";
import { useAuth } from "@/hooks/use-auth";
import PropTypes from "prop-types";
import { getAstrologerUnreadCount } from "@/api/notifications";

// Polling interval for the bell badge. 60s is gentle on the server
// and good enough for a feed UI — push/socket updates can shorten
// this later. Pause-on-blur skipped intentionally: a backgrounded
// browser tab won't fire timers anyway.
const POLL_INTERVAL_MS = 60_000;

export default function Header({ name, shouldGoBack = false }) {
  const navigate = useNavigate();
  const { astrologer } = useAuth();
  const astrologerName = astrologer?.personalInformation?.name || "";
  const profilePicture = astrologer?.personalInformation?.picture || "";
  const astrologerId = astrologer?.astrologerId || null;

  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!astrologerId) return;
    const { data } = await getAstrologerUnreadCount(astrologerId);
    if (typeof data?.unreadCount === "number") {
      setUnreadCount(data.unreadCount);
    }
  }, [astrologerId]);

  useEffect(() => {
    refresh();
    if (!astrologerId) return undefined;
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [astrologerId, refresh]);

  return (
    <div
      className={cn(
        "sticky top-0 z-20 w-full flex justify-between items-center bg-white h-16 pt-2 gap-1 mb-3 rounded-lg",
        shouldGoBack ? "pl-4" : " pl-8"
      )}
    >
      <div className={shouldGoBack ? "flex items-center gap-2" : ""}>
        {shouldGoBack && (
          <FaAngleLeft
            onClick={() => navigate(-1)}
            className="cursor-pointer"
            color="black"
            size={25}
          />
        )}
        {name !== "Dashboard" ? (
          <h1 className="font-medium text-xl text-black">{name}</h1>
        ) : (
          <h1 className="font-medium text-xl text-black">
            {"Shubh Prabhat!"}
            <span className="text-orange-600"> {astrologerName}</span>
          </h1>
        )}
      </div>
      <div className="flex justify-center items-center gap-3 md:mr-5 mr-20">
        <FiCreditCard size={20} />

        <button
          type="button"
          onClick={() => navigate("/notifications")}
          className="relative cursor-pointer"
          aria-label="Notifications"
        >
          <MdNotificationsNone size={22} />
          {unreadCount > 0 && (
            <span
              className={cn(
                "absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full",
                "bg-orange-500 text-white text-[10px] font-semibold",
                "flex items-center justify-center"
              )}
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        <div
          className="h-10 w-10 rounded-full cursor-pointer"
          onClick={() => navigate("/profile")}
        >
          {profilePicture ? (
            <img
              src={profilePicture}
              alt="Astrologer Picture"
              className="h-full w-full rounded-full object-cover"
            />
          ) : (
            <div className="h-full w-full rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500 text-sm">
                {astrologerName?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

Header.propTypes = {
  name: PropTypes.string.isRequired,
  shouldGoBack: PropTypes.bool,
};
